<template>
  <v-dialog
    v-model="showActionDialog"
    :fullscreen="fullscreen"
    min-height="95vh"
    :max-width="fullscreen ? undefined : '100rem'"
    persistent
    scrollable
  >
    <v-card v-if="activeOrder" class="action-dialog">
      <v-card-title
        class="d-flex justify-space-between pb-0"
        :class="{ 'cancelled-order': activeOrder.cancelled, 'integrated-background': integrated }"
      >
        <div>
          <order-priority-chip clickable :order="activeOrder" size="small" />
          <editable-order-name
            v-if="activeOrder.reference_name"
            class="mr-1"
            :disabled="!canChangeName"
            :order="activeOrder"
            @update:order="updateOrder"
          />
          <order-type-chip-menu clickable :order="activeOrder" size="small" />
          <cancelled-chip :order="activeOrder" size="small" />
          <div class="mt-2 qtm-body-small">
            <div class="text-mid-grey">
              <copy-text copy-on-click :text="activeOrder.qtm_reference_number" />
              - {{ activeOrder.state ? activeOrder.state.internal_name : '' }}
              <span v-if="activeOrder.transition_timestamp">
                - {{ timeDelta }} in state
              </span>
            </div>
            <admin-link :to="`rfqs/order/${activeOrder.id}/change/`">
              Open in Admin
            </admin-link>
            <reminder-notification-btn v-if="activeOrder.owner" :order="activeOrder" />
          </div>
        </div>

        <div v-if="activeOrder" class="qtm-body-small">
          <div v-if="activeOrder.date_closes">
            <div class="text-mid-grey qtm-overline">
              Quote Due
            </div>
            <div>
              {{ dateCloses }}
            </div>
          </div>
          <div v-if="activeOrder.delivery_date">
            <div class="text-mid-grey qtm-overline">
              Fulfillment Date
            </div>
            <div>
              {{ deliveryDate }}
            </div>
          </div>
        </div>

        <div class="d-flex mr-n2 mt-n2 qtm-body-small">
          <div v-if="company">
            <linked-company-name :order="activeOrder" />
            <div class="text-mid-grey" v-text="activeOrder.owner.company.purchasing_email" />
          </div>
          <qtm-icon-btn
            color="mid-grey"
            :icon="pinIcon"
            :loading="loading"
            :title="isOrderPinned ? 'Unpin' : 'Pin'"
            @click="pinOrder"
          />
          <qtm-icon-btn
            color="mid-grey"
            icon="mdi-refresh"
            :loading="loading"
            title="Refresh"
            @click="refreshOrder"
          />
          <qtm-icon-btn
            color="mid-grey"
            :icon="fullscreen ? 'mdi-window-restore' : 'mdi-window-maximize'"
            title="Resize"
            @click="toggleFullscreen"
          />
          <qtm-icon-btn
            color="mid-grey"
            icon="mdi-close"
            title="Close"
            @click="showActionDialog = false"
          />
        </div>
      </v-card-title>

      <v-divider class="mb-4" />

      <v-card-text v-if="!activeOrder.lastFullRefresh">
        <qtm-skeleton />
      </v-card-text>
      <v-card-text v-else class="dialog-content pa-2">
        <order-action-sidebar v-if="showSidebar" class="action-dialog-sidebar" :order="activeOrder" />

        <div v-if="showAttachment" class="action-dialog-attachmet-viewer">
          <attachment-viewer
            ref="pdfViewer"
            select-text-layer
            :src="documentToPreviewSrc"
            :name="documentToPreviewName"
            @close="showAttachment = false"
          />
        </div>

        <div class="action-dialog-component flex-grow-1 pr-4">
          <v-tabs
            v-model="tab"
            bg-color="background"
            class="action-dialog-tabs text-mid-light-grey"
            color="interactive"
          >
            <v-tab
              v-for="t in tabs"
              :key="t.key"
              class="text-uppercase"
              selected-class="text-interactive v-tab--selected"
            >
              {{ t.label }}
            </v-tab>
          </v-tabs>
          <v-window v-model="tab" class="action-dialog-content bg-background">
            <v-window-item key="main">
              <component
                :is="contentComponent"
                ref="activeComponent"
                :half-width="showAttachment"
                :invoices="invoices"
                :order="activeOrder"
                @preview="showDocumentPreview($event)"
              />
            </v-window-item>
            <v-window-item key="inbound-emails">
              <view-emails
                v-model:initial-email="initialEmail"
                :order="activeOrder"
                type="inbound"
                :visible="tab === 1"
              />
            </v-window-item>
            <v-window-item key="outbound-emails">
              <view-emails :order="activeOrder" type="outbound" :visible="tab === 2" />
            </v-window-item>
            <v-window-item key="attachments">
              <view-attachments :invoices="invoices" :order="activeOrder" @preview="showDocumentPreview($event)" />
            </v-window-item>
            <v-window-item key="event-log">
              <order-event-timeline :now="now" :order="activeOrder" :visible="tab === 4" />
            </v-window-item>
          </v-window>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="justify-space-between">
        <common-actions :order="activeOrder" />
        <order-state-actions
          :execute-hook="executeActionHook"
          :order="activeOrder"
          @refresh-component="refreshComponent"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { DateTime, type Invoice, OrderState } from '@quotetome/materials-api'
import { dateMonth, objectDeliveryDate } from '@/models/filters'
import AdminLink from '@/components/admin/admin-link.vue'
import AttachmentViewer from '@/components/attachments/attachment-viewer.vue'
import CancelledChip from '@/components/admin/cancelled-chip.vue'
import CommonActions from '@/components/admin/actions/common-actions.vue'
import CopyText from '@/components/copy-text.vue'
import EditableOrderName from '@/components/orders/editable-order-name.vue'
import LinkedCompanyName from '@/components/admin/linked-company-name.vue'
import OrderTypeChipMenu from '@/components/admin/order-type-chip-menu.vue'
import OrderActionSidebar from '@/components/admin/order-action-sidebar.vue'
import OrderEventTimeline from '@/components/admin/order-event-timeline.vue'
import OrderPriorityChip from '@/components/admin/order-priority-chip.vue'
import OrderStateActions from '@/components/admin/order-state-actions.vue'
import ReminderNotificationBtn from '@/components/admin/reminder-notification-btn.vue'
import { usePins } from '@/composables/pins'
import useTimeDelta from '@/composables/time-delta'
import ViewAttachments from '@/components/admin/view-attachments.vue'
import ViewEmails from '@/components/admin/emails/view-emails.vue'

// 1 minute
const nowUpdateInterval = 60 * 1000

const adminStore = useAdminStore()
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const { $api, $error } = useNuxtApp()

const activeOrder = computed(() => adminStore.activeOrder)
const orderEmail = computed(() => adminStore.orderEmail)
const isStaff = computed(() => authStore.isStaff)
const stateTransitionTime = computed(
  () => (activeOrder.value ? new DateTime(activeOrder.value.transition_timestamp * 1000) : null)
)

const now = ref(new DateTime())
const timeDelta = useTimeDelta(now, stateTransitionTime)
const { addPin, isPinned, removePin } = usePins()

const documentToPreviewSrc = ref()
const documentToPreviewName = ref()
const fullscreen = ref(false)
const invoices = ref<Invoice[]>([])
const initialEmail = ref()
const loading = ref(false)
const showAttachment = ref(false)
const tab = ref(0)

const activeComponent = useTemplateRef('activeComponent')

const tabs = [
  { label: 'Main', key: 'main' },
  { label: 'Inbox', key: 'inbound-emails' },
  { label: 'Sent Emails', key: 'outbound-emails' },
  { label: 'Attachments', key: 'attachments' },
  { label: 'Event Log', key: 'event-log' },
]

const canChangeName = computed(() => {
  return [
    OrderState.INVITING_VENDORS,
    OrderState.WAITING_FOR_QUOTES,
    OrderState.QUOTE_QA,
    OrderState.WAITING_FOR_PURCHASE_ORDER,
    OrderState.FINAL_ADJUSTMENTS,
    OrderState.WAITING_FOR_APPROVAL,
  ]
    .map(state => state.id)
    .includes(activeOrder.value?.state?.id)
})

const company = computed(() => activeOrder.value?.owner?.company)

const dateCloses = computed(() => dateMonth(activeOrder.value?.date_closes))

const deliveryDate = computed(() => objectDeliveryDate(activeOrder.value?.pos?.[0] || activeOrder.value))

const integrated = computed(() => !!activeOrder.value?.jobsite?.accounting_id)

const isOrderPinned = computed(() => isPinned(activeOrder.value?.id, 'order'))

const pinIcon = computed(() => isOrderPinned.value ? 'mdi-pin-off' : 'mdi-pin')

const showActionDialog = computed({
  get: () => !!activeOrder.value && isStaff.value,
  set: () => {
    showAttachment.value = false
    documentToPreviewSrc.value = null
    documentToPreviewName.value = null
    adminStore.activateOrder(undefined)
  }
})

const showSidebar = computed(() => !showAttachment.value)

const contentComponent = computed(() => {
  if (activeOrder.value?.state) {
    const component = activeOrder.value.state.dialog_component

    if (typeof component === 'function') {
      return component(activeOrder.value)
    }
    return component
  }
  return undefined
})

watch(activeOrder, async (newOrder, oldOrder) => {
  if (route.query.activeOrder?.toString() !== newOrder?.id?.toString() && newOrder?.id !== oldOrder?.id) {
    router.replace({
      ...route,
      query: {
        activeOrder: activeOrder.value?.id,
      }
    })
  }

  if (!newOrder) {
    tab.value = 0
    return
  }

  if (oldOrder && (newOrder.id === oldOrder.id) && newOrder.lastFullRefresh) {
    return
  }

  try {
    const order = await $api.v1.rfqs.get(activeOrder.value.id)
    adminStore.updateOrder(order)

    invoices.value = await $api.v1.rfqs.invoices(order.id)
  }
  catch (error) {
    $error.report(error)
  }

  if (orderEmail.value) {
    initialEmail.value = orderEmail.value
    tab.value = tabs.findIndex(t => t.key === 'inbound-emails')
    adminStore.clearOrderEmail()
  }
}, { immediate: true })

let nowUpdateTimer: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  nowUpdateTimer = setInterval(() => { now.value = new DateTime() }, nowUpdateInterval)
  if (route.query.activeOrder) {
    adminStore.activateOrder({ id: parseInt(route.query.activeOrder as string, 10) } as any)
  }
})

onBeforeUnmount(() => {
  clearInterval(nowUpdateTimer)
})

const executeActionHook = (hookName: string, action: any, abort: () => void) => {
  const component = activeComponent.value as any
  if (!component || !component[hookName]) {
    return undefined
  }
  return component[hookName](action, abort)
}

const pinOrder = () => {
  if (isOrderPinned.value) {
    removePin(activeOrder.value.id, 'order')
  }
  else {
    addPin({
      title: activeOrder.value.reference_name,
      type: 'order',
      objectId: activeOrder.value.id,
    })
  }
}

const refreshComponent = async () => {
  const component = activeComponent.value as any

  if (component && component.refresh) {
    // Allow the order to be set before refreshing components that need it
    await nextTick()
    component.refresh()
  }
}

const refreshOrder = async () => {
  if (activeOrder.value) {
    loading.value = true
    try {
      const refreshedOrder = await $api.v1.rfqs.get(activeOrder.value.id)
      adminStore.updateOrder(refreshedOrder)

      invoices.value = await $api.v1.rfqs.invoices(refreshedOrder.id)

      await refreshComponent()
    }
    catch (error) {
      $error.report(error)
    }
    loading.value = false
  }
}

const showDocumentPreview = (attachment: { name: string; url: string }) => {
  documentToPreviewName.value = attachment.name
  documentToPreviewSrc.value = attachment.url
  showAttachment.value = true
}

const toggleFullscreen = () => {
  fullscreen.value = !fullscreen.value
}
</script>

<style lang="scss" scoped>
.action-dialog {
  background-color: rgb(var(--v-theme-background));
}

.v-card .dialog-content {
  color: inherit;
}

.order-type-chip {
  margin-left: 1rem;
}

.admin-link {
  font-weight: bold;
}

.v-card .dialog-content {
  color: inherit;
  display: flex;
  padding: 0 0 1rem 0.25rem;
}

.action-dialog-sidebar {
  overflow-y: auto;
  width: 18rem;
  flex-shrink: 0;
  margin-right: 1rem;
}

.action-dialog-attachmet-viewer {
  flex-shrink: 0;
  overflow-y: auto;
  width:48%;
}

.action-dialog-component {
  overflow-y: auto;
  min-width: 50%;
}

.action-dialog-tabs {
  position: absolute;
  width: 100%;
  z-index: 100;
}

.action-dialog-content {
  padding-top: 50px;
}

.cancelled-order {
  border-top: solid 6px rgb(var(--v-theme-error));
}

.integrated-background {
  background-color: rgb(var(--v-theme-sand));
}

.v-tabs-items {
  padding-top: 10px;
}
</style>
